// src/shared/changelog/getChangelog.ts
import type { AxiosInstance } from "axios";

import { ChangelogMonthGroup, groupEntriesByMonth } from "./grouping";
import { fetchChangelogEntries } from "./request/request";
import { ChangelogEntry } from "./types";

/**
 * Changelog data provider — the single seam the UI depends on (CTX-202).
 *
 * Phase 1 returned a hardcoded module so the page could ship before the
 * endpoint existed; this now reads `GET /api/v2/changelog`, which is the only
 * implementation of the visibility rule. Nothing downstream re-filters.
 *
 * The axios instance is a parameter rather than something this module builds,
 * because the only correct one comes from `useAxiosAuth0()` — a hook — and it
 * carries both the Auth0 token interceptor and the 401 → `/login` redirect.
 * Callers are components and hooks, so obtaining it costs them nothing.
 */

/**
 * One in-flight request per session, shared by every caller.
 *
 * Two surfaces need this data — the page and the footer chip's unseen dot —
 * and they mount independently. Without the memo the chip would fire its own
 * request on every app load, doubling the traffic to show a month label. The
 * feed only changes when a production deploy publishes, so a value that lives
 * as long as the tab is not stale in any way a reader would notice, and a page
 * reload gets fresh data.
 */
let inFlight: Promise<ChangelogEntry[]> | null = null;

/** All entries the tenant may see, newest-first. */
export async function getChangelog(
  client: AxiosInstance,
): Promise<ChangelogEntry[]> {
  if (!inFlight) {
    inFlight = fetchChangelogEntries(client)
      .then((entries) => sortNewestFirst(entries))
      .catch((error) => {
        // A failed attempt must not be cached: the chip and the page would
        // both inherit the failure for the rest of the session, and a
        // transient network blip would look permanent.
        inFlight = null;
        throw error;
      });
  }
  return inFlight;
}

/** Test seam, and the escape hatch if a surface ever needs to force a refetch. */
export function resetChangelogCache(): void {
  inFlight = null;
}

/**
 * `localeCompare` on the `YYYY-MM-DD` string rather than `Date` parsing: the
 * format sorts lexicographically, and a same-day batch keeps the server's
 * order, which already breaks ties deterministically by id.
 */
function sortNewestFirst(entries: ChangelogEntry[]): ChangelogEntry[] {
  return [...entries].sort((a, b) => b.releasedAt.localeCompare(a.releasedAt));
}

/**
 * The newest month's entries — the "release" the What's new modal walks
 * through, newest entry first.
 *
 * A month, not a day, is the batch (CTX-224). The endpoint does not serialize
 * deployment identity, so `releasedAt` is the finest grain a client can see,
 * and the month is the grouping the reader already meets on `/changelog`. At
 * the historical ~5 entries/month a day-batch would almost always be a single
 * entry, which is a walkthrough with nothing to walk.
 *
 * `null` when the feed is empty — the caller must render nothing rather than an
 * empty dialog.
 */
export async function getLatestReleaseGroup(
  client: AxiosInstance,
): Promise<ChangelogMonthGroup | null> {
  const [newest] = groupEntriesByMonth(await getChangelog(client));
  return newest ?? null;
}
